import { createReducer } from '@reduxjs/toolkit';
import {
	InfoWaterProState,
	keyforAnalysisGroup,
	keyForResultSets,
	keyforSimulation,
	keyforTable,
	keyforTableMetadata,
	STATUS_ENUM,
} from '../types';
import {
	requestInfoWaterProAnalysisGroups,
	requestInfoWaterProAnalysisGroupsResolved,
	requestInfoWaterProAnalysisGroupsRejected,
	requestInfoWaterProSimulations,
	requestInfoWaterProSimulationsResolved,
	requestInfoWaterProSimulationsRejected,
	requestInfoWaterProTablesResolved,
	requestInfoWaterProTablesRejected,
	requestInfoWaterProTables,
	requestInfoWaterProMetadataResolved,
	requestInfoWaterProMetadataRejected,
	requestInfoWaterProMetadata,
	testOnlyInfoWaterProAnalysisGroupsLoading,
	testOnlyInfoWaterProSimulationsLoading,
	testOnlyInfoWaterProTablesLoading,
	testOnlyInfoWaterProMetadataLoading,
	requestResultSetsResolved,
	requestResultSetsRejected,
	requestResultSets,
	deleteInfoWaterProSimulationResolved,
	setMapSimulationsLoading,
} from '../actions/infoWaterPro.actions';
import { removeDuplicatedSimulations } from '../utils';

export const initialInfoWaterProState: InfoWaterProState = {
	analysisGroups: {},
	simulations: {},
	tables: {},
	tableMetadata: {},
	resultSets: {},
	mapSimulations: {
		initialized: false,
		status: STATUS_ENUM.IDLE,
		data: undefined,
	}, // This is used to track simulations that are being loaded
};

export default createReducer(initialInfoWaterProState, builder =>
	builder
		.addCase(
			requestInfoWaterProAnalysisGroupsResolved,
			(state, { payload }) => {
				const key = keyforAnalysisGroup(payload.dbid);
				state.analysisGroups[key] = {
					initialized: true,
					status: STATUS_ENUM.SUCCESS,
					data: payload.data,
				};
			},
		)
		.addCase(
			requestInfoWaterProAnalysisGroupsRejected,
			(state, { payload }) => {
				const key = keyforAnalysisGroup(payload.dbid);
				state.analysisGroups[key] = {
					initialized: true,
					status: STATUS_ENUM.ERROR,
					data: [],
					error: payload.error,
				};
			},
		)
		.addCase(setMapSimulationsLoading, (state, action) => {
			if (action.payload) {
				state.mapSimulations.status = STATUS_ENUM.PENDING;
				state.mapSimulations.initialized = false;
				return;
			}
			state.mapSimulations.status = STATUS_ENUM.IDLE;
		})
		.addCase(requestInfoWaterProAnalysisGroups, (state, { payload }) => {
			const key = keyforAnalysisGroup(payload.dbid);
			if (!(key in state.analysisGroups)) {
				state.analysisGroups[key] = {
					initialized: true,
					status: STATUS_ENUM.PENDING,
					data: [],
				};
			}
		})
		.addCase(
			testOnlyInfoWaterProAnalysisGroupsLoading,
			(state, { payload }) => {
				const key = keyforAnalysisGroup(payload.dbid);
				state.analysisGroups[key] = {
					initialized: true,
					status: STATUS_ENUM.PENDING,
					data: [],
				};
			},
		)

		.addCase(
			requestInfoWaterProSimulationsResolved,
			(state, { payload }) => {
				const key = keyforSimulation(
					payload.dbid,
					payload.analysisGroupId,
				);
				let simulations = payload.data;

				if (payload.filterDuplicates) {
					simulations = removeDuplicatedSimulations(payload.data);
				}

				state.simulations[key] = {
					initialized: true,
					status: STATUS_ENUM.SUCCESS,
					data: simulations,
				};
			},
		)
		.addCase(
			requestInfoWaterProSimulationsRejected,
			(state, { payload }) => {
				const key = keyforSimulation(
					payload.dbid,
					payload.analysisGroupId,
				);
				state.simulations[key] = {
					initialized: true,
					status: STATUS_ENUM.ERROR,
					data: [],
					error: payload.error,
				};
			},
		)
		.addCase(requestInfoWaterProSimulations, (state, { payload }) => {
			if (payload.analysisGroupId === '' || payload.dbid === '') {
				return;
			}
			const key = keyforSimulation(payload.dbid, payload.analysisGroupId);
			state.simulations[key] = {
				initialized: true,
				status: STATUS_ENUM.PENDING,
				data: [],
			};
		})
		.addCase(
			testOnlyInfoWaterProSimulationsLoading,
			(state, { payload }) => {
				const key = keyforSimulation(
					payload.dbid,
					payload.analysisGroupId,
				);
				state.simulations[key] = {
					initialized: true,
					status: STATUS_ENUM.PENDING,
					data: [],
				};
			},
		)

		.addCase(requestInfoWaterProTablesResolved, (state, { payload }) => {
			const key = keyforTable(payload.dbid, payload.simulationId);
			state.tables[key] = {
				initialized: true,
				status: STATUS_ENUM.SUCCESS,
				data: payload.data,
			};
		})
		.addCase(requestInfoWaterProTablesRejected, (state, { payload }) => {
			const key = keyforTable(payload.dbid, payload.simulationId);
			state.tables[key] = {
				initialized: true,
				status: STATUS_ENUM.ERROR,
				data: [],
				error: payload.error,
			};
		})
		.addCase(requestInfoWaterProTables, (state, { payload }) => {
			if (payload.simulationId === '' || payload.dbid === '') {
				return;
			}
			const key = keyforTable(payload.dbid, payload.simulationId);
			if (!(key in state.tables)) {
				state.tables[key] = {
					initialized: true,
					status: STATUS_ENUM.PENDING,
					data: [],
				};
			}
		})
		.addCase(testOnlyInfoWaterProTablesLoading, (state, { payload }) => {
			const key = keyforTable(payload.dbid, payload.simulationId);
			state.tables[key] = {
				initialized: true,
				status: STATUS_ENUM.PENDING,
				data: [],
			};
		})

		.addCase(requestInfoWaterProMetadataResolved, (state, { payload }) => {
			const key = keyforTableMetadata(
				payload.dbid,
				payload.simulationId,
				payload.tableId,
			);
			state.tableMetadata[key] = {
				initialized: true,
				status: STATUS_ENUM.SUCCESS,
				data: payload.data,
			};
		})
		.addCase(requestInfoWaterProMetadataRejected, (state, { payload }) => {
			const key = keyforTableMetadata(
				payload.dbid,
				payload.simulationId,
				payload.tableId,
			);
			state.tableMetadata[key] = {
				initialized: true,
				status: STATUS_ENUM.ERROR,
				data: { attributes: [], ids: [], timesteps: [] },
				error: payload.error,
			};
		})
		.addCase(requestInfoWaterProMetadata, (state, { payload }) => {
			if (
				payload.simulationId === '' ||
				payload.dbid === '' ||
				payload.tableId === ''
			) {
				return;
			}
			const key = keyforTableMetadata(
				payload.dbid,
				payload.simulationId,
				payload.tableId,
			);
			if (!(key in state.tableMetadata)) {
				state.tableMetadata[key] = {
					initialized: true,
					status: STATUS_ENUM.PENDING,
					data: { attributes: [], ids: [], timesteps: [] },
				};
			}
		})
		.addCase(testOnlyInfoWaterProMetadataLoading, (state, { payload }) => {
			const key = keyforTableMetadata(
				payload.dbid,
				payload.simulationId,
				payload.tableId,
			);
			state.tableMetadata[key] = {
				initialized: true,
				status: STATUS_ENUM.PENDING,
				data: { attributes: [], ids: [], timesteps: [] },
			};
		})
		.addCase(requestResultSets, (state, { payload }) => {
			const { assets, dbId } = payload;
			assets.forEach(({ assetId, assetType }) => {
				const resultSetKey = keyForResultSets(dbId, assetType, assetId);
				state.resultSets[resultSetKey] = {
					initialized: true,
					status: STATUS_ENUM.PENDING,
					data: [],
				};
			});
		})
		.addCase(requestResultSetsResolved, (state, { payload }) => {
			const { assetType, data, dbId, objectId } = payload;
			const key = keyForResultSets(dbId, assetType, objectId);
			state.resultSets[key] = {
				initialized: true,
				status: STATUS_ENUM.SUCCESS,
				data,
			};
		})
		.addCase(requestResultSetsRejected, (state, { payload }) => {
			const { assetType, dbId, objectId, error } = payload;
			const key = keyForResultSets(dbId, assetType, objectId);
			state.resultSets[key] = {
				initialized: true,
				status: STATUS_ENUM.ERROR,
				data: [],
				error,
			};
		})
		.addCase(deleteInfoWaterProSimulationResolved, (state, { payload }) => {
			if (payload.dbid === '' || !payload.simulationId) {
				return;
			}
			const key = keyforTable(payload.dbid, payload.simulationId);
			if (key in state.tables) {
				delete state.tables[key];
			}
			const keySim = keyforSimulation(
				payload.dbid,
				payload.analysisGroupId,
			);
			if (keySim in state.simulations) {
				state.simulations[keySim].data = state.simulations[
					keySim
				].data?.filter(n => n.oid.toString() != payload.simulationId);
			}
		}),
);
